import { API, graphqlOperation } from "aws-amplify";
import {
  batchMutations,
  isConditionalCheckFailed,
  logError,
} from "./appUtils";
import { forcePersistTrackCars } from "./trackCarPersistence";
import {
  compareSortKeys,
  generateTimestampSortKey,
  getSortKeyForPosition,
} from "./timestampSortKey";
import { getLocationFullName } from "./locationUtils";

import { updateTrackCarMini } from "../graphql/trackCar/trackCar-mutations";
import { VIRTUAL_UNSPOTTED_TRACK } from "./trackListUtils";

/**
 * The one key a moving car is booked under everywhere in a move. TrackCar rows
 * carry their shipment under shipmentId while lean shipment rows only have id,
 * and calculateMoveState keys originalTrackIds/trackCarMoves this way — so any
 * later lookup into those maps must derive the key identically, or a row that
 * carries a shipmentId misses and its source track reads as unknown.
 */
export const getTrackItemKey = (item) => item?.shipmentId || item?.id;

/**
 * Logic-only utility to deduplicate track state.
 * STAFF LEVEL OPTIMIZATION: Preserves array references if no changes are made
 * to prevent unnecessary re-renders of unaffected tracks.
 */
export const dedupeTrackState = (state) => {
  const result = { ...state };
  let mapChanged = false;

  Object.entries(state || {}).forEach(([trackId, list]) => {
    const seen = new Set();
    let listChanged = false;
    const dedupedList = (list || []).filter((item) => {
      if (!item?.id) return false;
      if (seen.has(item.id)) {
        listChanged = true;
        return false;
      }
      seen.add(item.id);
      return true;
    });

    if (listChanged) {
      result[trackId] = dedupedList;
      mapChanged = true;
    }
  });

  return mapChanged ? result : state;
};

/**
 * Pure function to determine new state after a move.
 * Identifies moving items, calculates their new positions, and generates sort keys.
 *
 * STAFF LEVEL OPTIMIZATION: Removed cloneDeep. Performs surgical updates
 * on the state map to ensure only affected tracks change their references.
 *
 * @returns {Object} { workingState, itemsToInsert, affectedTrackIds, originalTrackIds, trackCarMoves }
 */
export const calculateMoveState = ({
  shipmentIds,
  targetTrackId,
  dropTarget,
  baseTrackData,
  trackDataOverride,
  finalVisualState,
  isUnspottedTrack,
  tracks,
}) => {
  const movingIdsSet = new Set(shipmentIds);
  const affectedTrackIds = new Set([targetTrackId]);
  const originalTrackIds = new Map();
  const trackCarMoves = new Map();

  // ELITE STAFF LEVEL FIX: Resolve target location ID and full name for syncing.
  // We MUST ensure we are using the Location ID (not Aisle ID) for the TrackCar record.
  const targetTrack = (tracks || []).find(
    (t) =>
      t.id === targetTrackId || t.locations?.items?.[0]?.id === targetTrackId,
  );
  const targetLocation = targetTrack?.locations?.items?.[0];
  const resolvedTargetTrackId = targetLocation?.id || targetTrackId;

  // STAFF LEVEL FIX: Ensure consistent Track naming format (e.g. "T-1")
  let targetLocationFullName = null;
  if (targetLocation) {
    const areaType = targetLocation.area?.areaType || targetLocation.areaType;
    if (areaType === "TRACK" || targetLocation.areaId === "TRACK") {
      const prefix =
        targetLocation.area?.prefix || targetLocation.prefix || "T";
      const name = targetLocation.name || targetLocation.aisle?.name || "";
      targetLocationFullName = `${prefix}-${name}`;
    } else {
      targetLocationFullName = getLocationFullName(targetLocation);
    }
  }

  // 1. Prepare working state (Surgical Clone)
  // Instead of cloneDeep, we use a shallow clone of the map.
  // We will only clone individual track arrays as we modify them.
  let workingState = { ...baseTrackData }; // Shallow clone of the map

  const movingItems = [];

  // 2. Extract moving items and identify their source tracks
  const isMovingItem = (item) =>
    movingIdsSet.has(item?.id) ||
    (item?.shipmentId && movingIdsSet.has(item.shipmentId));

  Object.keys(workingState).forEach((trackId) => {
    const trackList = workingState[trackId];
    if (!trackList) return;

    const hasMovingItems = trackList.some((item) => isMovingItem(item));
    if (!hasMovingItems) return;

    // We found a track that contains moving items. Clone it surgically.
    const remainingItems = [];
    trackList.forEach((item) => {
      if (isMovingItem(item)) {
        const itemKey = getTrackItemKey(item);
        movingItems.push({ ...item });
        originalTrackIds.set(itemKey, trackId);
        affectedTrackIds.add(trackId);

        if (trackId !== resolvedTargetTrackId) {
          if (isUnspottedTrack(resolvedTargetTrackId)) {
            trackCarMoves.set(itemKey, {
              fromTrackId: trackId,
              toTrackId: resolvedTargetTrackId,
              trackCarId: item.id,
              shipment: item,
              unplacing: true,
            });
          } else {
            trackCarMoves.set(itemKey, {
              fromTrackId: trackId,
              toTrackId: resolvedTargetTrackId,
              trackCarId: item.id,
              shipment: item,
            });
          }
        }
      } else {
        remainingItems.push(item);
      }
    });

    workingState[trackId] = remainingItems; // Updated array reference
  });

  if (movingItems.length === 0) return null;

  // Ensure trackId on non-moving items when missing
  Object.keys(workingState).forEach((trackId) => {
    const list = workingState[trackId];
    if (!Array.isArray(list) || list.length === 0) return;
    const isUnspotted = isUnspottedTrack(trackId);
    workingState[trackId] = list.map((item) => {
      if (!item || isMovingItem(item)) return item;
      const next = { ...item };
      if (!isUnspotted && !next.trackId) {
        next.trackId = trackId;
      }
      return next;
    });
  });

  // 3. Sort moving items based on visual order
  if (finalVisualState) {
    const targetFinalList = finalVisualState[resolvedTargetTrackId] || [];

    const positionMap = new Map();
    targetFinalList.forEach((item, idx) => positionMap.set(item.id, idx));

    movingItems.sort((a, b) => {
      const pos = (id) =>
        positionMap.has(id) ? positionMap.get(id) : Number.MAX_SAFE_INTEGER;
      const posA = pos(a.id);
      const posB = pos(b.id);
      if (posA === posB) {
        return compareSortKeys(
          a.sortKey ?? "",
          b.sortKey ?? "",
        );
      }
      return posA - posB;
    });
  } else {
    movingItems.sort((a, b) =>
      compareSortKeys(a.sortKey ?? "", b.sortKey ?? ""),
    );
  }

  // 4. Calculate insertion index
  // Ensure target track is cloned if not already
  if (!workingState[resolvedTargetTrackId]) {
    workingState[resolvedTargetTrackId] = [];
  } else if (
    workingState[resolvedTargetTrackId] === baseTrackData[resolvedTargetTrackId]
  ) {
    // Clone when target track was untouched earlier to avoid mutating base reference
    workingState[resolvedTargetTrackId] = [
      ...workingState[resolvedTargetTrackId],
    ];
  }

  let targetList = workingState[resolvedTargetTrackId];
  let insertIndex = targetList.length;

  if (dropTarget && typeof dropTarget.index === "number") {
    const desiredIndex = dropTarget.index;
    const targetTrackOriginal = baseTrackData[resolvedTargetTrackId] || [];
    const isSameTrackMove = Array.from(originalTrackIds.values()).some(
      (trackId) => trackId === resolvedTargetTrackId,
    );

    if (finalVisualState) {
      const finalTargetList = finalVisualState[resolvedTargetTrackId] || [];
      const movingBefore = finalTargetList
        .slice(0, desiredIndex)
        .filter((item) => movingIdsSet.has(item.id)).length;

      insertIndex = desiredIndex - movingBefore;
    } else if (isSameTrackMove) {
      let movingItemsBeforeOrAtIndex = 0;
      targetTrackOriginal.forEach((item, idx) => {
        if (idx <= desiredIndex && movingIdsSet.has(item.id)) {
          movingItemsBeforeOrAtIndex++;
        }
      });

      const itemAtDesiredIndex = targetTrackOriginal[desiredIndex];
      const isDesiredIndexMoving =
        itemAtDesiredIndex && movingIdsSet.has(itemAtDesiredIndex.id);

      if (isDesiredIndexMoving) {
        insertIndex = desiredIndex - movingItemsBeforeOrAtIndex + 1;
      } else {
        let movingItemsBeforeIndex = 0;
        targetTrackOriginal.forEach((item, idx) => {
          if (idx < desiredIndex && movingIdsSet.has(item.id)) {
            movingItemsBeforeIndex++;
          }
        });
        insertIndex = desiredIndex - movingItemsBeforeIndex;
      }
    } else {
      insertIndex = desiredIndex;
    }

    insertIndex = Math.min(targetList.length, Math.max(0, insertIndex));
  }

  // 5. Map items to insert with new metadata
  const itemsToInsert = movingItems.map((item) => {
    const itemKey = getTrackItemKey(item);
    const originalTrackId = originalTrackIds.get(itemKey);
    const isTargetUnspotted = isUnspottedTrack(resolvedTargetTrackId);
    const mappedItem = {
      ...item,
      trackId: isTargetUnspotted
        ? VIRTUAL_UNSPOTTED_TRACK.id
        : resolvedTargetTrackId,
      // STAFF LEVEL FIX: Never fall back to previous locationFullName when moving to a new track.
      // If we don't have a target name yet, use a placeholder or null to trigger backend lookup.
      locationFullName: isTargetUnspotted ? "" : targetLocationFullName || "",
      ...(!isTargetUnspotted &&
        targetLocation && {
          areaId: targetLocation.areaId ?? null,
          zoneId: targetLocation.zoneId ?? null,
          aisleId: targetLocation.aisleId ?? null,
        }),
    };

    if (isTargetUnspotted) {
      mappedItem.active = "true";
      mappedItem.sortKey = item.sortKey ?? "";
    } else {
      mappedItem.active = "true";
      // Moving from UNSPOTTED to a physical Track: always update existing TrackCar.
      trackCarMoves.set(itemKey, {
        active: "true",
        fromTrackId: originalTrackId,
        toTrackId: resolvedTargetTrackId,
        trackCarId: item.id,
        shipment: mappedItem,
      });
    }

    return mappedItem;
  });

  // 6. Update target track and generate sort keys
  targetList.splice(insertIndex, 0, ...itemsToInsert);
  // targetList is already a reference to workingState[targetTrackId]

  const prevItem = insertIndex > 0 ? targetList[insertIndex - 1] : null;
  const nextItemIndex = insertIndex + itemsToInsert.length;
  const nextItem =
    nextItemIndex < targetList.length ? targetList[nextItemIndex] : null;

  const prevKey = prevItem?.sortKey ?? null;
  let nextKey = nextItem?.sortKey ?? null;

  // Correction logic if keys are unsorted
  if (prevKey && nextKey && compareSortKeys(nextKey, prevKey) < 0) {
    for (let i = nextItemIndex; i < targetList.length; i++) {
      const candidateKey = targetList[i]?.sortKey;
      if (candidateKey && compareSortKeys(candidateKey, prevKey) > 0) {
        nextKey = candidateKey;
        break;
      }
    }
    if (nextKey && compareSortKeys(nextKey, prevKey) < 0) {
      nextKey = null;
    }
  }

  let lastKey = prevKey;
  itemsToInsert.forEach((item, i) => {
    let newSortKey = generateTimestampSortKey(lastKey, nextKey, targetList);

    // Midpoint sequence correction if necessary
    if (nextKey && compareSortKeys(newSortKey, nextKey) >= 0) {
      try {
        const nextKeyParts = nextKey.split("#");
        if (nextKeyParts.length === 2) {
          const nextTime = new Date(nextKeyParts[0]).getTime();
          if (!isNaN(nextTime) && nextTime > 0) {
            const correctedTime = nextTime - 1;
            const collisionSortKey = new Date(correctedTime).toISOString();
            newSortKey = `${collisionSortKey}#9999`;
          }
        }
      } catch (e) {
        /* collision resolution applied */
      }
    }

    item.sortKey = newSortKey;
    lastKey = newSortKey;
    targetList[insertIndex + i] = item;
  });

  return {
    workingState,
    itemsToInsert,
    affectedTrackIds,
    originalTrackIds,
    trackCarMoves,
  };
};

/**
 * Find the original car in originalTrackData by id (scan all tracks).
 */
const findOriginalCar = (originalTrackData, carId) => {
  if (!originalTrackData || !carId) return { car: null, trackId: "" };
  const entries = Object.entries(originalTrackData);
  for (let i = 0; i < entries.length; i++) {
    const [tid, list] = entries[i];
    const car = (list || []).find((c) => c.id === carId);
    if (car) return { car, trackId: tid };
  }
  return { car: null, trackId: "" };
};

/**
 * Build a single list of TrackCar update inputs for the move: every car on
 * affected tracks whose trackId or sortKey changed vs originalTrackData.
 * Single batch of update inputs; no separate persistTrackCars pass.
 * Uses affectedTrackIds (including unspotted) so moves to/from unspotted are persisted.
 */
export const buildTrackCarUpdatesForMove = ({
  workingState,
  originalTrackData,
  affectedTrackIds,
  isUnspottedTrack,
  resolveParentType,
}) => {
  const resolveParentTypeValue = (item) => {
    if (!item) return null;
    if (item.parentType) return item.parentType;
    return typeof resolveParentType === "function"
      ? resolveParentType(item)
      : null;
  };

  const trackIds = Array.from(affectedTrackIds);
  const updates = [];
  for (let t = 0; t < trackIds.length; t++) {
    const trackId = trackIds[t];
    const list = workingState[trackId] || [];
    for (let i = 0; i < list.length; i++) {
      const car = list[i];
      if (!car?.id) continue;
      const original = findOriginalCar(originalTrackData, car.id);
      const currentSortKey = car.sortKey ?? "";
      const originalSortKey = original.car?.sortKey ?? "";
      const currentTrackId = trackId;
      const originalTrackId = original.trackId || "";
      const needsUpdate =
        !original.car ||
        currentTrackId !== originalTrackId ||
        currentSortKey !== originalSortKey;
      if (!needsUpdate) continue;

      const parentType = resolveParentTypeValue(car);
      updates.push({
        id: car.id,
        trackId: currentTrackId,
        active: car.active === "false" ? "false" : "true",
        sortKey: currentSortKey,
        eodTrackId: car.eodTrackId ?? car.trackId ?? currentTrackId,
        eodSortKey: car.eodSortKey ?? car.sortKey ?? "",
        locationFullName: car.locationFullName || "",
        parentType,
        shipmentId: car.shipmentId || car.id,
        railCarNumber: car.railCarNumber,
        accountId: car.accountId,
        reloadYardId: car.reloadYardId,
        ...(car.areaId && { areaId: car.areaId }),
        ...(car.zoneId && { zoneId: car.zoneId }),
        ...(car.aisleId && { aisleId: car.aisleId }),
      });
    }
  }
  return updates;
};

/**
 * Handles the async persistence logic for moving items.
 * Single batch of updateTrackCar (via updateTrackCarMini); no second persistTrackCars pass.
 */
export const performMovePersistence = async ({
  workingState,
  affectedTrackIds,
  setLoading,
  setTrackData,
  notify,
  onSuccess,
  originalTrackData,
  targetTrackId,
  isUnspottedTrack,
  resolveParentType,
  tracks,
}) => {
  const trackIdsToPersist = Array.from(affectedTrackIds).filter(
    (id) => !isUnspottedTrack(id),
  );

  setLoading(true);

  try {
    const resequenceTracks = () => {
      trackIdsToPersist.forEach((trackId) => {
        const list = workingState[trackId] || [];
        if (!list.length) return;
        const sorted = [...list].sort((a, b) =>
          compareSortKeys(a.sortKey ?? "", b.sortKey ?? ""),
        );
        workingState[trackId] = sorted.map((item, idx) => {
          const newKey = getSortKeyForPosition(idx, sorted, true);
          return { ...item, sortKey: newKey };
        });
      });
    };

    const shouldRetrySortKey = (err) => {
      const msg = err?.message || err?.errors?.[0]?.message || "";
      return (
        isConditionalCheckFailed(err) ||
        msg.includes("TransactionConflict") ||
        msg.toLowerCase().includes("sort") ||
        msg.toLowerCase().includes("duplicate")
      );
    };

    // Persist only cars that actually changed (usually just the moved car).
    // calculateMoveState already assigns a valid sortKey to the moved car.
    // Full resequence runs only on retry when we hit a sort-key conflict.
    const runTrackCarPersistence = async () => {
      const allUpdates = buildTrackCarUpdatesForMove({
        workingState,
        originalTrackData,
        affectedTrackIds,
        isUnspottedTrack,
        resolveParentType,
      });
      if (allUpdates.length === 0) return;
      const results = await batchMutations(
        allUpdates.map(
          (input) => () =>
            API.graphql(graphqlOperation(updateTrackCarMini, { input })),
        ),
        { batchSize: 100, maxRetries: 3 },
      );
      if (results.some((r) => !r.success))
        throw new Error("Failed to move track cars between tracks");
    };

    try {
      await runTrackCarPersistence();
    } catch (err) {
      if (shouldRetrySortKey(err)) {
        resequenceTracks();
        await runTrackCarPersistence();
      } else {
        throw err;
      }
    }

    setTrackData(workingState, targetTrackId);

    if (onSuccess) onSuccess();
    return Array.from(affectedTrackIds).filter((id) => !isUnspottedTrack(id));
  } catch (err) {
    // ELITE ROLLBACK ENGINE
    console.error("Move persistence failed, initiating rollback...", err);

    // 1. Revert UI state immediately
    setTrackData(originalTrackData);

    try {
      // 2. Revert TrackCar sequence changes
      await forcePersistTrackCars(
        trackIdsToPersist,
        originalTrackData,
        null,
        tracks,
      );
    } catch (rollbackError) {
      logError("performMovePersistence:rollback", rollbackError, null);
    }

    if (notify) {
      notify("Move failed and was rolled back. Please retry.", "error");
    }

    throw err;
  } finally {
    setLoading(false);
  }
};
